import {useContext, useState, useEffect} from "react";
import classNames from "classnames";
import {CSSTransition} from 'react-transition-group';
import {observer} from "mobx-react";
import Menu from "react-metismenu";
import Button from "../../../utilities/Button";
import {ErrorBoundary} from "../../../utilities/ErrorBoundary";
import FontAwesome from "../../../utilities/FontAwesome";
import {primaryNavHeight} from "../../../../utils/Settings";
import {StoreContext} from "../../../../stores/StoreLoader";
import {getDistrictUrl, isSchoolBlocksApp} from "../../../../utils/SchoolBlocksUtilities";
import NavigationLink from "../NavigationLink";
import HelpScout from "../../../support/HelpScout";
import {AdminController, AuthenticationController, UserController} from "../../../../backends/YiiRoutes";
import styles from "./MobileNav.module.scss";
import {LogoutButton} from "../../../admin/sidebar/items/LogoutButton";

interface DefaultScrollLinkProps {
    children: JSX.Element | undefined,
    to: string,
}

function DefaultScrollLink({children, to}: DefaultScrollLinkProps) {
    const {interfaceStore} = useContext(StoreContext);
    function handleOnClick() {
        interfaceStore.scrollToId(to)
    }

    const metislinkButtonClassName = classNames({
        [styles.metislinkButton]: true,
        ['metismenu-link']: true,
    });
    return <Button className={metislinkButtonClassName} onClick={handleOnClick}>
        {children}
    </Button>
}

function DefaultLink({
    className,
    classNameActive,
    classNameHasActiveChild,
    active,
    hasActiveChild,
    to,
    externalLink,
    hasSubMenu,
    toggleSubMenu,
    activateMe,
    children,
    navigationObj,
    subMenuVisibility,
}) {
    const linkClassName = classNames(
        className,
        active && classNameActive,
        hasActiveChild && classNameHasActiveChild,
    )

    if (to === "/logout") {
        return <LogoutButton className={linkClassName}>
            {children}
        </LogoutButton>
    }

    return <NavigationLink
        navigationObj={navigationObj ? navigationObj : {}} // static links like Login won't have a navigationObj defined
        className={linkClassName}
        href={to}
        onClick={hasSubMenu ? toggleSubMenu : activateMe}
        target={externalLink ? '_blank' : undefined}
        aria-expanded={hasSubMenu ? subMenuVisibility : undefined}
        aria-haspopup={hasSubMenu}
    >
        {children}
    </NavigationLink>
}

const transitionClassNames = {
    enter: styles.transitionEnter,
    enterActive: styles.transitionEnterActive,
    enterDone: styles.transitionEnterDone,
    exit: styles.transitionExit,
    exitActive: styles.transitionExitActive,
    exitDone: styles.transitionExitDone,
};

function isExternalLink(navigationObj) {
    return isSchoolBlocksApp() ? true : navigationObj.external_url === 'string' && navigationObj.external_url.length > 0;
}

export const userMenuContent = (userStore, i18nStore) => {
    const menu: Array<IMenuItem> = [];
    if (!userStore.id) {
        menu.push({
            'icon': "fas fa-sign-in-alt",
            'label': "Login",
            'externalUrl': false,
            'to': AuthenticationController.login(i18nStore.currentOrgSupportedLocale),
        })
    } else {
        menu.push({
            icon: 'fas fa-user',
            'label': `${userStore.firstName} ${userStore.lastName}`,
            'to': UserController.settings(i18nStore.currentOrgSupportedLocale),
            'externalUrl': false,
        },
                  {
                      'label': 'Logout',
                      'to': "/logout",
                      'icon': 'fas fa-sign-out-alt',
                      'externalUrl': false,
                  }, {
                      'label': 'My Calendar',
                      'to': UserController.myCalendar(i18nStore.currentOrgSupportedLocale),
                      'icon': 'fas fa-calendar-alt',
                      'externalUrl': false,
                  }, {
                      'label': 'Following',
                      'to': UserController.following(i18nStore.currentOrgSupportedLocale),
                      'icon': 'fas fa-star',
                      'externalUrl': false,
                  }, {
                      'label': 'My Feed',
                      'to': UserController.feed(i18nStore.currentOrgSupportedLocale),
                      'icon': 'fas fa-rss',
                      'externalUrl': false,
                  }, {
                      'label': 'My Information',
                      'to': UserController.settings(i18nStore.currentOrgSupportedLocale),
                      'icon': 'fas fa-cog',
                      'externalUrl': false,
                  }
        );

        if (userStore.isAdmin) {
            menu.push({
                'label': 'Configuration',
                'to': AdminController.organizationSettings(i18nStore.currentOrgSupportedLocale),
                'icon': 'fas fa-cog',
                'externalUrl': false,
            })
        }
    }

    return menu;
}

export const navigationContent = (organizationStore) => {
    let navTree;
    if (organizationStore.navigation.length > 0) {
        navTree = organizationStore.navigationTree
            .filter(item => item.enabled)
            .map(item => {
                const children = item.children
                    .filter(item => item.enabled)
                    .map(item => {
                        const grandchildren = item.children
                            .filter(child => child.enabled)
                            .map(child => {
                                return {
                                    icon: "empty-icon",
                                    label: child.title,
                                    externalUrl: isExternalLink(child),
                                    id: child.id,
                                    navigationObj: child,
                                };
                            });
                        const settings: IMenuItem = {
                            icon: "empty-icon",
                            label: item.title,
                            id: item.id,
                            externalUrl: isExternalLink(item),
                            navigationObj: item,
                        };
                        if (grandchildren && grandchildren.length > 0) {
                            settings.content = grandchildren;
                        }
                        return settings;
                    });
                const settings: IMenuItem = {
                    icon: item.icon || "",
                    label: item.title,
                    externalUrl: isExternalLink(item),
                    id: item.id,
                    navigationObj: item,

                };
                if (children && children.length > 0) {
                    settings.content = children;
                }
                return settings;
            });
    } else {
        navTree = Array.from(organizationStore.blocks.values()).filter((block: {key: any}) => {
            return organizationStore.block_config.get(block.key);
        })
            .map((item: ICleverSiteBlock) => {
                return {
                    icon: "",
                    label: item.title,
                    to: item.key,
                    externalUrl: false,
                    id: item.id,
                    primaryNavHeight: primaryNavHeight,
                }
            });
    }

    const districtUrl = getDistrictUrl(organizationStore.organization);
    if (isSchoolBlocksApp() && districtUrl) {
        navTree.unshift({
            icon: "fas fa-university",
            label: "District",
            to: districtUrl,
            externalUrl: true,
        })
    }

    return navTree;
}

const MobileNav = observer((props: MobileNavProps) => {
    const {organizationStore, userStore} = useContext(StoreContext);
    const {isOpen, toggleOpen, identifier} = props;
    const contentFunction = () => props.content;
    const [content, setContent] = useState(contentFunction());
    const [incomingFocusElement, setIncomingFocusElement] = useState<Element | null>(null);

    const mobileMenuContainerClassName = classNames({
        [styles.mobileMenuContainer]: true,
    });
    const iconStateNameHidden = classNames({
        "fas": true,
        "fa-caret-right": true,
        [styles.stateIcon]: true,
    });
    const iconStateNameVisible = classNames({
        "fas": true,
        "fa-caret-down": true,
        [styles.stateIcon]: true,
    });

    const handleKeydown = (evt) => {
        if (evt.code === "Escape") {
            handleClose();
        }
    }
    const handleClose = () => {
        if (isOpen) toggleOpen();
    }
    const focusCloseButton = () => {
        const closeButton = document.getElementById(identifier) as HTMLButtonElement;
        if (closeButton) closeButton.focus();
    }

    useEffect(() => {
        setContent(contentFunction());
    }, [organizationStore.navigation, userStore.user])

    useEffect(() => {
        if (isOpen) {
            setIncomingFocusElement(document.activeElement);
            focusCloseButton();
        } else if (!isOpen && incomingFocusElement) (incomingFocusElement as HTMLLinkElement).focus();

        window.addEventListener('keydown', handleKeydown);
        return () => {
            window.removeEventListener('keydown', handleKeydown);
        }
    }, [isOpen]);

    return (
        <ErrorBoundary>
            <span>
                <div className={mobileMenuContainerClassName}>
                    <CSSTransition in={isOpen} classNames={transitionClassNames} timeout={0}>
                        <nav className={styles.navContainer} role={'navigation'}>
                            {isOpen && <div className={styles.exitContainer}>
                                <Button className={styles.mobileClose}
                                        aria-label={'Close Navigation'}
                                        onClick={handleClose}
                                        id={identifier}
                                >
                                    <span className={styles.closeText}>Close</span>
                                    <FontAwesome ariaHidden={true} prefix={'fas'} name={"fa-times"}/>
                                </Button>
                            </div>}
                            {isOpen && <Menu
                                // changing the key on open/close ensures the menu re-renders and closes all open items.
                                key={identifier + isOpen}
                                className={styles.menuContainer}
                                classNameContainerVisible={styles.menuContainerVisible}
                                classNameItem={styles.menuItem}
                                classNameItemHasVisibleChild={styles.hasVisibleChild}
                                classNameLink={styles.link}
                                classNameLinkActive={styles.activeLink}
                                classNameLinkHasActiveChild={styles.activeLinkChild}
                                content={content}
                                classNameIcon={styles.metisMenuIcon}
                                iconNameStateHidden={iconStateNameHidden}
                                iconNameStateVisible={iconStateNameVisible}
                                LinkComponent={organizationStore.navigation.length > 0 ? DefaultLink : DefaultScrollLink}
                            />}
                            <span tabIndex={0}
                                  onFocus={focusCloseButton}
                            />
                        </nav>
                    </CSSTransition>
                    <HelpScout/>
                </div>
            </span>
        </ErrorBoundary>
    );
})

interface MobileNavProps {
    isOpen: boolean,
    toggleOpen: () => void,
    identifier: string,
    content: Array<IMenuItem>,
}

export default MobileNav;
